import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, newsService } from '../services/api';
import { NewsItem } from '../types';
import RichNewsEditor from '../components/RichNewsEditor';
import '../styles/AdminNews.css';
import '../styles/RichNewsEditor.css';

const emptyNews: NewsItem = {
    title: '',
    content: '',
    summary: '',
    category: 'announcements',
    author: 'Admin',
    imageUrl: '',
    isPublished: true
};

const AdminNews: React.FC = () => {
    const navigate = useNavigate();
    const [newsList, setNewsList] = useState<NewsItem[]>([]);
    const [selected, setSelected] = useState<NewsItem | null>(null);
    const [form, setForm] = useState<NewsItem>(emptyNews);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const checkAccessAndLoad = async () => {
            if (!authService.isAuthenticated()) {
                navigate('/login');
                return;
            }
            if (!(await authService.checkIsAdmin())) {
                navigate('/');
                return;
            }
            await loadNews();
        };

        checkAccessAndLoad();
    }, [navigate]);

    const loadNews = async () => {
        try {
            setLoading(true);
            const data = await newsService.getAllNews();
            setNewsList(data);
        } catch (err) {
            console.error('Erro ao carregar notícias', err);
            setError('Não foi possível carregar as notícias.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (item: NewsItem | null) => {
        setSelected(item);
        if (item) {
            setForm({
                id: item.id,
                title: item.title,
                content: item.content,
                summary: item.summary,
                category: item.category,
                author: item.author,
                imageUrl: item.imageUrl,
                isPublished: item.isPublished
            });
        } else {
            setForm(emptyNews);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const target = e.target;
        const { name, value } = target;

        // Checkbox (isPublished)
        if (target instanceof HTMLInputElement && target.type === 'checkbox') {
            setForm((prev) => ({
                ...prev,
                [name]: target.checked
            }));
        } else {
            setForm((prev) => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            if (form.id) {
                await newsService.updateNews(form.id, {
                    title: form.title,
                    content: form.content,
                    summary: form.summary,
                    category: form.category,
                    author: form.author,
                    imageUrl: form.imageUrl,
                    isPublished: form.isPublished
                });
            } else {
                await newsService.createNews({
                    title: form.title,
                    content: form.content,
                    summary: form.summary,
                    category: form.category,
                    author: form.author,
                    imageUrl: form.imageUrl,
                    isPublished: form.isPublished
                });
            }
            await loadNews();
            handleSelect(null);
        } catch (err) {
            console.error('Erro ao salvar notícia', err);
            setError('Não foi possível salvar a notícia.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (item: NewsItem) => {
        if (!item.id) return;
        if (!window.confirm(`Excluir a notícia "${item.title}"?`)) return;
        try {
            await newsService.deleteNews(item.id);
            await loadNews();
            if (selected?.id === item.id) {
                handleSelect(null);
            }
        } catch (err) {
            console.error('Erro ao excluir notícia', err);
            setError('Não foi possível excluir a notícia.');
        }
    };

    const formatDate = (value?: string) => {
        if (!value) return '';
        const d = new Date(value);
        return d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="admin-page admin-panel-content">
                <h1 className="admin-page-title">Notícias</h1>
                <p className="admin-page-subtitle">
                    Publique notícias que aparecerão na Home e na página de News. URLs no texto (ex: https://...)
                    serão exibidas como links clicáveis no site.
                </p>

                {error && <div className="admin-error">{error}</div>}

                <div className="admin-grid">
                    <div className="admin-list">
                        <div className="admin-list-header">
                            <h2>Notícias</h2>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => handleSelect(null)}
                            >
                                Nova notícia
                            </button>
                        </div>

                        {loading ? (
                            <div className="admin-loading">Carregando notícias...</div>
                        ) : (
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Título</th>
                                        <th>Categoria</th>
                                        <th>Publicado</th>
                                        <th>Criada em</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {newsList.map((item) => (
                                        <tr
                                            key={item.id}
                                            className={selected?.id === item.id ? 'selected' : ''}
                                            onClick={() => handleSelect(item)}
                                        >
                                            <td>{item.title}</td>
                                            <td>{item.category}</td>
                                            <td>{item.isPublished ? 'Sim' : 'Não'}</td>
                                            <td>{formatDate(item.createdAt)}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-small btn-danger"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(item);
                                                    }}
                                                >
                                                    Excluir
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {newsList.length === 0 && !loading && (
                                        <tr>
                                            <td colSpan={5}>Nenhuma notícia cadastrada ainda.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="admin-form-wrapper">
                        <h2>{form.id ? 'Editar notícia' : 'Nova notícia'}</h2>
                        <form className="admin-form" onSubmit={handleSubmit}>
                            <div className="form-row">
                                <label htmlFor="title">Título</label>
                                <input
                                    id="title"
                                    name="title"
                                    type="text"
                                    value={form.title}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-row">
                                <label htmlFor="summary">Resumo (aparece na Home)</label>
                                <textarea
                                    id="summary"
                                    name="summary"
                                    value={form.summary}
                                    onChange={handleChange}
                                    rows={3}
                                />
                            </div>

                            <div className="form-row">
                                <label htmlFor="content">Conteúdo completo</label>
                                <RichNewsEditor
                                    value={form.content}
                                    onChange={(html) => setForm((prev) => ({ ...prev, content: html }))}
                                    placeholder="Escreva o conteúdo da notícia. Links, imagens e formatação são suportados."
                                />
                            </div>

                            <div className="form-row">
                                <label htmlFor="imageUrl">URL da imagem</label>
                                <input
                                    id="imageUrl"
                                    name="imageUrl"
                                    type="text"
                                    value={form.imageUrl}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="form-row-inline">
                                <div className="form-group">
                                    <label htmlFor="category">Categoria</label>
                                    <select
                                        id="category"
                                        name="category"
                                        value={form.category}
                                        onChange={handleChange}
                                    >
                                        <option value="announcements">Announcements</option>
                                        <option value="events">Events</option>
                                        <option value="patch-notes">Patch Notes</option>
                                        <option value="download">Download</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="author">Autor</label>
                                    <input
                                        id="author"
                                        name="author"
                                        type="text"
                                        value={form.author}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div className="form-row-checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        name="isPublished"
                                        checked={form.isPublished}
                                        onChange={handleChange}
                                    />
                                    Publicado
                                </label>
                            </div>

                            <div className="form-actions">
                                <button type="submit" className="btn btn-primary" disabled={saving}>
                                    {saving ? 'Salvando...' : 'Salvar notícia'}
                                </button>
                                {form.id && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => handleSelect(null)}
                                    >
                                        Limpar formulário
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
        </div>
    );
};

export default AdminNews;

